/**
 * Calculation and math helper functions for accessorials
 */

import { DEFAULT_PRICE_PER_MINUTE, DEFAULT_PAY_PER_MINUTE } from '../../constants/constants.js'
import type { Accessorial } from '../../types/accessorial.js'

/**
 * Round a value to the nearest penny (always rounds up)
 *
 * IMPORTANT: This function always rounds UP to the nearest penny, even if the
 * amount is only a fraction of a penny over. This is intentional for financial
 * calculations to prevent undercharging.
 *
 * @param value - Value to be rounded to nearest penny
 * @returns Rounded number
 *
 * @example
 * roundToNearestPenny(10.001) // 10.01
 * roundToNearestPenny(10.999) // 11.00
 * roundToNearestPenny(10.00) // 10.00
 */
export function roundToNearestPenny(value: number): number {
  return Math.ceil(value * 100) / 100
}

/**
 * Calculate delay price to charge customer based on idle time
 *
 * @param driverIdleTimeMinutes - Minutes the driver was idle
 * @param customerPricePerMinute - Price per minute rate (optional, uses default if not provided)
 * @returns Total price to charge customer
 */
export function calcDelayPrice(
  driverIdleTimeMinutes: number,
  customerPricePerMinute?: number
): number {
  const minutes = driverIdleTimeMinutes || 0
  const pricePerMinute = customerPricePerMinute ?? DEFAULT_PRICE_PER_MINUTE
  return minutes * pricePerMinute
}

/**
 * Calculate delay pay to driver based on idle time
 *
 * @param driverIdleTimeMinutes - Minutes the driver was idle
 * @param driverPayPerMinute - Pay per minute rate (optional, uses default if not provided)
 * @returns Total pay to driver
 */
export function calcDelayPay(
  driverIdleTimeMinutes: number,
  driverPayPerMinute?: number
): number {
  const minutes = driverIdleTimeMinutes || 0
  const payPerMinute = driverPayPerMinute ?? DEFAULT_PAY_PER_MINUTE
  return minutes * payPerMinute
}

/**
 * Calculate partial move price based on move status
 *
 * Returns a fraction of the move cost based on how far along the move progressed.
 *
 * @param moveStatus - Current status of the move
 * @param moveCost - Total cost/price of the move
 * @returns Partial price based on move progress
 */
export function calcPartialPrice(moveStatus: string | null = null, moveCost: number = 0): number {
  let partialPrice = 0

  switch (moveStatus) {
    case 'pickup started':
      partialPrice = moveCost * 0.25
      break
    case 'pickup arrived':
      partialPrice = moveCost * 0.4
      break
    case 'pickup successful':
      partialPrice = moveCost * 0.5
      break
    case 'delivery started':
      partialPrice = moveCost * 0.75
      break
    case 'delivery arrived':
      partialPrice = moveCost * 0.9
      break
    case 'delivery successful':
      partialPrice = moveCost * 1
      break
    default:
      partialPrice = moveCost * 0.1
      break
  }

  return partialPrice
}

/**
 * Calculate partial move pay to driver based on move status
 *
 * Returns a fraction of the move pay based on how far along the move progressed.
 *
 * @param moveStatus - Current status of the move
 * @param movePay - Total pay for the move
 * @returns Partial pay based on move progress
 */
export function calcPartialPay(moveStatus: string | null = null, movePay: number = 0): number {
  let partialPay = 0

  switch (moveStatus) {
    case 'pickup started':
      partialPay = movePay * 0.25
      break
    case 'pickup arrived':
      partialPay = movePay * 0.4
      break
    case 'pickup successful':
      partialPay = movePay * 0.5
      break
    case 'delivery started':
      partialPay = movePay * 0.75
      break
    case 'delivery arrived':
      partialPay = movePay * 0.9
      break
    case 'delivery successful':
      partialPay = movePay * 1
      break
    default:
      partialPay = movePay * 0.1
      break
  }

  return partialPay
}

/**
 * Merge two accessorials together
 *
 * Uses the primary accessorial as the base and adds the secondary accessorial's
 * cost, ar_amount, and ap_amount values to it.
 *
 * @param primaryAccessorial - Base accessorial to merge into
 * @param secondaryAccessorial - Accessorial whose amounts will be added
 * @returns Merged accessorial
 * @throws Error if either accessorial is invalid
 */
export function mergeAccessorials(
  primaryAccessorial: Accessorial,
  secondaryAccessorial: Accessorial
): Accessorial {
  // Validation
  if (!primaryAccessorial) {
    throw new Error('Invalid primary accessorial!')
  }
  if (!secondaryAccessorial) {
    throw new Error('Invalid secondary accessorial!')
  }

  // Merge accessorial amounts
  const mergedAccessorial: Accessorial = {
    ...primaryAccessorial,
    cost: roundToNearestPenny(primaryAccessorial.cost + secondaryAccessorial.cost),
    ar_amount: roundToNearestPenny(primaryAccessorial.ar_amount + secondaryAccessorial.ar_amount),
    ap_amount: roundToNearestPenny(primaryAccessorial.ap_amount + secondaryAccessorial.ap_amount),
  }

  return mergedAccessorial
}
